import { filter } from 'lodash';
import { useMemo } from 'react';

import HtmlComponentErrorBoundary from 'hb-builder/components/RenderComponents/shared/single/Html/HtmlComponentErrorBoundary';
import { HTML_COMPONENT_NAME } from 'hb-builder/constants/HtmlComponentName';
import { useRenderComponentsContext } from 'hb-builder/contexts/ComponentsContext';
import { cleanHtml } from 'hb-builder/utils/item';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';

// Keeping this component in this file to prevent circular dependencies
// but still exporting it to be used in other files
export const ChildrenRenderer = ({ item }) => {
  const children = item?.children?.filter(Boolean);

  return children.map((child) => <TraverseItem key={child.id} item={child} />);
};

export const TraverseItem = ({ item }: { item: DocumentItem }) => {
  const { components } = useRenderComponentsContext();
  const Component = components?.[item?.type];

  const children = useMemo(() => {
    if (Component) {
      return item?.children && filter(item.children, Boolean)?.length > 0 ? (
        <ChildrenRenderer item={item} />
      ) : (
        cleanHtml(item)
      );
    }

    return null;
  }, [Component, item]);

  if (!Component) {
    return null;
  }

  // There's a special case for the Html component,
  // as it needs to be wrapped in an error boundary to not have a potential JS error crash the whole app
  if (Component.componentName === HTML_COMPONENT_NAME) {
    return (
      <HtmlComponentErrorBoundary>
        <Component.Render key={item.id} item={item}>
          {children}
        </Component.Render>
      </HtmlComponentErrorBoundary>
    );
  }

  return (
    <Component.Render key={item.id} item={item}>
      {children}
    </Component.Render>
  );
};
